module Client.IllyAdmin.Actions.Loggers.Overlay

open Client
open Elmish
open Fable.React
open Fable.React.Props
open Fable.Remoting.Client
open Illy.SafeStack.Client
open Illy.SafeStack.Client.jQueryUI
open Illy.SafeStack
open Shared.IllyAdmin.Loggers
open Shared.IllyAdmin.Loggers.Validations
open Shared.Apis
open Shared.Collections
open Shared
open SafeStack.Client
open SafeStack.Client.HTMLIdentifier


module Row =
    type RowId = HTMLIdentifier

    type Msg =
        | ModeMsg of NoFlags Select.Msg
        | FilepathMsg of Text.Msg
        | SeverityMsg of NoFlags Select.Msg

    type Model =
        {
            Id: RowId
            Data: LoggerMetadata
            Mode: NoFlags Select.Model
            Filepath: Text.Model
            Severity: NoFlags Select.Model
            Errors: string option
        }

    module Init =
        let outputMode rowId outputMode =
            let outputOptions =
                [
                    OutputMode.File None
                    OutputMode.Database
                ] |> List.map OutputMode.toListOption
                |> ListType.Standard

            let selectedOption =
                outputMode
                |> Option.defaultValue (OutputMode.File None)
                |> OutputMode.toListOption
                |> ListOptionType.Standard

            Select.initWithOptions (sprintf "%s-output" (HTMLIdentifier.toString rowId)) "" (Some "Select an Output Mode") true outputOptions (Some selectedOption)

        let filepath rowId fileName defaultFilepath value =
            let placeholder =
                match defaultFilepath with
                | None -> sprintf "%s.txt" fileName
                | Some (dfp: string) ->
                    if dfp.EndsWith '\\' then
                        sprintf "%s%s.txt" dfp fileName
                    else if dfp.Contains '.' then
                        dfp
                    else
                        sprintf "%s\\%s.txt" dfp fileName
                        
            Text.CustomSettings.defaultOptions ()
            |> Text.init (sprintf "%s-filepath" (HTMLIdentifier.toString rowId)) "Filepath" (Some placeholder) 512.0 false value

        let severity rowId severity =
            let severityOptions =
                [
                    Severity.Debug
                    Severity.Info
                    Severity.Error
                ] |> List.map Severity.toListOption
                |> ListType.Standard

            let selectedOption = severity |> Option.map (Severity.toListOption >> ListOptionType.Standard)

            Select.initWithOptions (sprintf "%s-severity" (HTMLIdentifier.toString rowId)) "" (Some "All") false severityOptions selectedOption

    let init (data: LoggerMetadata) defaultFilepath outputMode filepath severity =
        let id = sprintf "loggers-overlay-row-%s" data.Key |> HTMLIdentifier.ofStringOrFail
        {
            Id = id
            Data = data
            Mode = Init.outputMode id outputMode
            Filepath = Init.filepath id data.Key defaultFilepath filepath
            Severity = Init.severity id severity
            Errors = None
        }

    let update msg model =
        match msg with
        | ModeMsg tMsg ->
            let m,cmd = Select.update tMsg model.Mode
            { model with Mode = m }, Cmd.map ModeMsg cmd
        | FilepathMsg fpMsg ->
            let m,cmd = Text.update fpMsg model.Filepath
            { model with Filepath = m }, Cmd.map FilepathMsg cmd
        | SeverityMsg sMsg ->
            let m,cmd = Select.update sMsg model.Severity
            { model with Severity = m }, Cmd.map SeverityMsg cmd

type GenerationState =
    | Idle
    | Generating
    | Downloaded

module GenerationState =
    let isGenerating =
        function
        | Generating -> true
        | _ -> false

type Model =
    {
        GenerationState: GenerationState
        Application: string
        DefaultPath: Text.Model
        DefaultPathErrors: string option
        Rows: Row.Model list
        PresentationMethod: NoFlags Select.Model
        Preview: string option
    }

type Msg =
    | DefaultPathMsg of Text.Msg
    | RowMsg of string * Row.Msg
    | PresentationMethodMsg of NoFlags Select.Msg
    | GenerateConfigMsg
    | FileDownloaded of ConfigDownloadResult
    | PreviewGenerated of ConfigPreviewResult
    | CancelMsg
    | CloseOverlay
    | ApiError of ApiError

type Effect =
    | ApiError of ApiError
    | CloseOverlay

module Calls =
    let preview dto =
        Api.secureCall<ILoggersIllyAdminApi,_,_,_>
            SecuredRoute.IllyAdmin
            (fun api -> api.PreviewConfigFile)
            dto
            PreviewGenerated
            Msg.ApiError

    let download dto =
        Api.secureCall<ILoggersIllyAdminApi,_,_,_>
            SecuredRoute.IllyAdmin
            (fun api -> api.DownloadConfigFile)
            dto
            FileDownloaded
            Msg.ApiError

module Init =
    let presentationMethod method =
        let methods =
            [
                PresentationMethod.Download
                PresentationMethod.OnScreen
            ] |> List.map PresentationMethod.toListOption
            |> ListType.Standard
        let selectedMethod =
            method
            |> (PresentationMethod.toListOption >> ListOptionType.Standard)
            |> Some

        Select.initWithOptions "loggers-overlay-presentation-method" "Presentation Method" None true methods selectedMethod

    let defaultPath value =
        Text.CustomSettings.defaultOptions ()
        |> Text.init "loggers-overlay-output-path" "Output Path" (Some "Enter the default output directory or filepath") 384.0 false value

let init (loggers: LoggerMetadata NonEmptyList) =
    {
        GenerationState = GenerationState.Idle
        Application = (loggers |> NonEmptyList.head).Application
        DefaultPath = Init.defaultPath None
        DefaultPathErrors = None
        Rows = loggers |> NonEmptyList.map (fun l -> Row.init l None None None None) |> NonEmptyList.toList
        PresentationMethod = Init.presentationMethod PresentationMethod.Download
        Preview = None
    }

let private generateDto model =
    let getSeverity = Select.toListOption >> Option.map (ListOption.getId >> Severity.ofInt)
    {
        DefaultFilepath = model.DefaultPath |> Text.toString
        Loggers = model.Rows |> List.map (fun r ->
            {
                Output =
                    match r.Mode |> Select.toListOption |> Option.map (ListOption.getId >> OutputMode.ofInt) with
                    | Some(OutputMode.Database) -> OutputMode.Database
                    | Some(OutputMode.File _) -> OutputMode.File (r.Filepath |> Text.toString)
                    | None -> OutputMode.File None
                Key = r.Data.Key
                Severity = r.Severity |> getSeverity |> Option.defaultValue Severity.All
            }
        )
    }

let private setValidations model errors =
    let defaultPathErrors =
        errors
        |> List.choose (function | DefaultPathError dpe -> Some dpe | _ -> None)
        |> List.map DefaultPathError.toString
        |> CompositionalValidation.concatOptional

    let loggerConfigErrors =
        errors
        |> List.choose (function | LoggerConfigError (k,e) -> Some (k,e) | _ -> None)
        |> ValidationInfrastructure.bundleValidations
            (fun (k,e) -> (model.Rows |> List.findIndex (fun r -> r.Data.Key = LoggerKey.unwrap k), e |> LoggerConfigError.toString))
            (model.Rows |> List.length)

    { model with
        GenerationState = GenerationState.Idle
        DefaultPathErrors = defaultPathErrors
        Rows = model.Rows |> List.mapi (fun i r -> { r with Errors = loggerConfigErrors[i] })
    }

let update msg model =
    match msg with
    | DefaultPathMsg odMsg ->
        let m,cmd = Text.update odMsg model.DefaultPath
        let newRows =
            model.Rows
            |> List.map ( fun r ->
                { r with
                    Filepath =
                        Row.Init.filepath
                            r.Id
                            r.Data.Key
                            (m |> Text.toString)
                            (r.Filepath |> Text.toString)
                })

        { model with
            DefaultPath = m
            Rows = newRows
        }, Cmd.map DefaultPathMsg cmd |> SideEffect.Cmd
    | RowMsg (id, rMsg) ->
        let m,cmd = model.Rows |> List.tryFind (fun r -> r.Data.Key = id) |> Option.defaultWith (fun _ -> failwithf "Could not find the row with id '%s'" id) |> Row.update rMsg
        let newRows = model.Rows |> ListFunctions.replace (fun r -> r.Data.Key) (fun _ -> m) id
        { model with Rows = newRows }, Cmd.map (fun c -> RowMsg (m.Data.Key, c)) cmd |> SideEffect.Cmd
    | PresentationMethodMsg pmMsg ->
        let m,cmd = Select.update pmMsg model.PresentationMethod
        { model with PresentationMethod = m }, Cmd.map PresentationMethodMsg cmd |> SideEffect.Cmd
    | GenerateConfigMsg ->
        let getPresentationMethod = Select.toListOption >> Option.map (ListOption.getId >> PresentationMethod.ofInt)
        let cmd =
            match model.PresentationMethod |> getPresentationMethod |> Option.defaultValue PresentationMethod.Download with
            | PresentationMethod.Download -> model |> generateDto |> Calls.download
            | PresentationMethod.OnScreen -> model |> generateDto |> Calls.preview

        let newRows = model.Rows |> List.map (fun r -> { r with Errors = None })

        { model with
            GenerationState = GenerationState.Generating
            Preview = None
            DefaultPathErrors = None
            Rows = newRows
        }, cmd |> SideEffect.Cmd
    | FileDownloaded res ->
        match res with
        | ROP.Success (fileName, fileContents) ->
            fileContents.SaveFileAs(fileName)
            { model with GenerationState = GenerationState.Downloaded }, SideEffect.none
        | ROP.Failure errors ->
            setValidations model errors, SideEffect.none
    | PreviewGenerated res ->
        match res with
        | ROP.Success preview ->
            { model with Preview = Some preview; GenerationState = GenerationState.Idle }, SideEffect.none
        | ROP.Failure errors ->
            setValidations model errors, SideEffect.none
    | CancelMsg
    | Msg.CloseOverlay ->
        model, Effect.CloseOverlay |> SideEffect.Event
    | Msg.ApiError err ->
        { model with GenerationState = GenerationState.Idle }, err |> Effect.ApiError |>  SideEffect.Event

module Render =
    let filepathInput (r: Row.Model) dispatch =
        div [ Class "input-with-extras" ] [
            yield Text.textInput false r.Filepath (Row.FilepathMsg >> (fun msg -> RowMsg (r.Data.Key, msg)) >> dispatch)
            match r.Errors with
            | Some errors ->
                yield div [ Class "validationIcon extras" ] [
                    FontAwesome.solid "exclamation-triangle" [] [ Class "animate-pulse-red fa-exclamation-triangle fas" ]
                    |> Tooltip.create [ str errors ]
                ]
            | None ->
                ()
        ]

let view model dispatch =
    jQueryUI.modal
        (sprintf "Generate Logger Config - %s" model.Application)
        1000
        Msg.CloseOverlay
        dispatch
        (Some 1280)
        [
            section [] [
                h3 [] [ str "Defaults" ]
                jQueryUI.label model.DefaultPath.Control.Id model.DefaultPath.Control.Label model.DefaultPathErrors None false
                Text.textInput false model.DefaultPath (DefaultPathMsg >> dispatch)
            ]
            section [ Id "selectedLoggers" ] [
                h3 [] [ str "Selected Loggers" ]
                jQueryUI.simpleTable
                    "No loggers selected"
                    [
                        Some [ str "Name" ] ===> fun (r: Row.Model) -> [
                            str r.Data.DisplayName
                        ]

                        Some [ str "Output Mode" ] ===> fun (r: Row.Model) -> [
                            Select.selectInput false r.Mode (Row.ModeMsg >> (fun msg -> RowMsg(r.Data.Key, msg)) >> dispatch)
                        ]

                        Some [ str "Output" ] ===> fun r -> [
                            let getMode = Select.toListOption >> Option.map (ListOption.getId >> OutputMode.ofInt)

                            match getMode r.Mode with
                            | Some (OutputMode.File _) -> Render.filepathInput r dispatch
                            | Some (OutputMode.Database) -> str "@Database"
                            | None -> div [] [ str "Please select an output mode" ]
                        ]

                        Some [ str "Filter" ] ===> fun r -> [
                            Select.selectInput false r.Severity (Row.SeverityMsg >> (fun msg -> RowMsg (r.Data.Key, msg)) >> dispatch)
                        ]
                    ]
                    false
                    model.Rows
            ]
            section [] [
                let loading = GenerationState.isGenerating model.GenerationState
                yield h3 [] [ str "Output" ]
                yield UI.renderError model.PresentationMethod.ValidationError
                yield Select.selectInput loading model.PresentationMethod (PresentationMethodMsg >> dispatch)
                yield span [ Style [ MarginLeft "10px" ] ] [
                    UI.button "Generate" GenerateConfigMsg dispatch
                    str " or "
                    a [ OnClick (fun _ -> dispatch CancelMsg) ] [ str "Cancel" ]
                ]
            ]
            section [] [
                match model.Preview with
                | Some preview ->
                    yield Fable.React.Standard.output [] [ str preview ]
                | None ->
                    ()
                match model.GenerationState with
                | GenerationState.Downloaded ->
                    yield jQueryUI.illy_info [
                        yield! [
                            str "To use the generated configuration, copy ";
                            code [] [ str "logging.config" ];
                            sprintf " to the application root for %s " model.Application |> str
                        ]
                        if model.Application = "CarePath" || model.Application = "LinksManager" then
                            yield! [
                                str "(typically ";
                                code [] [ sprintf "C:\\inetpub\\<Client> - <Environment>\\%s" model.Application |> str ];
                                str ").\n"
                            ]
                            yield str "Make sure to restart the application pool or the changes will not take effect"
                    ]
                | _ ->
                    ()
            ]
        ]